import type { ReferenceKind } from '../operations/reference-details.repository';
import { at, object, text } from './repository';
import type { PlantStorageLocation, Row, Value } from './types';

const masters: Record<string, string> = {
  product: 'products',
  customer: 'customers',
  supplier: 'suppliers',
  warehouse: 'warehouses',
  factory: 'factories',
  location: 'locations',
  origin_location: 'locations',
  destination_location: 'locations',
  storage_room: 'storage-rooms',
  room: 'storage-rooms',
  currency: 'currencies',
  from_currency: 'currencies',
  to_currency: 'currencies',
  carrier: 'carriers',
  issuing_entity: 'issuing-entities',
  payment_terms: 'payment-terms',
  recorder_configuration: 'logger-recorder-configurations',
  supply_route: 'supply-routes',
  route: 'supply-routes',
  per_uom: 'uoms',
  base_uom: 'uoms',
  uom: 'uoms',
  charge_type: 'charge-types',
  fab_phase: 'fab-phases',
  document_type: 'document-types',
};
const operations: Record<string, ReferenceKind> = {
  employee: 'employee',
  authorized: 'employee',
  signer_employee: 'employee',
  recipient: 'employee',
  shipment: 'shipment',
  shipment_line: 'shipment-line',
  batch_lot: 'lot',
  lot: 'lot',
  invoice: 'invoice',
  invoices: 'invoice',
  parent_invoice: 'invoice',
  corrects_invoice: 'invoice',
  current_invoice: 'invoice',
  billing_period: 'period',
  period: 'period',
  billing_run: 'run',
  report: 'report',
  supersedes_report: 'report',
  superseded_by_report: 'report',
  logger_file: 'logger',
  evaluation: 'evaluation',
  temperature_evaluation: 'evaluation',
  current_evaluation: 'evaluation',
  raised_evaluation: 'evaluation',
  latest_evaluation: 'evaluation',
  plant_record: 'plant-record',
  storage_import: 'storage-import',
  logistics_cost_invoice: 'cost-invoice',
  cost_breakdown: 'cost-breakdown',
  payable: 'payable',
  tariff_statement: 'statement',
  statement: 'statement',
  receipt_group: 'receipt-group',
  movement: 'movement',
  discrepancy: 'discrepancy',
  purchase_order_line: 'purchase-line',
};
const aliases: Record<string, string> = {
  batch_lot: 'lot',
  storage_room: 'room',
  recorder_configuration: 'recorder',
  issuing_entity: 'entity',
  supply_route: 'route',
};
export function isOpaque(value: Value | undefined): boolean {
  return (
    typeof value === 'string' &&
    /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value)
  );
}
export function isReference(key: string): boolean {
  return (
    key === 'id' ||
    /_ids?$/.test(key) ||
    /_by$/.test(key) ||
    ['by', 'actor', 'actor_sub', 'corrects'].includes(key)
  );
}
export function referenceKind(key: string, domain = ''): ReferenceKind | undefined {
  if (key === 'run_id' && domain === 'billing') return 'run';
  if (key === 'authorized_by') return 'employee';
  if (key === 'corrected_by' || key === 'corrects') return 'invoice';
  const prefix = key.replace(/_ids?$/, '');
  if (masters[prefix]) return `masterdata:${masters[prefix]}`;
  if (operations[prefix]) return operations[prefix];
  if (key.endsWith('_by') || ['by', 'actor', 'actor_sub'].includes(key)) return 'account';
  return undefined;
}
/** Joined/snapshot labels belong to this exact row, never an unrelated parent's product. */
function plantLocationLabel(row: PlantStorageLocation): string | undefined {
  return [row.plant_location_code, row.plant_location_name].filter(value => typeof value === 'string' && value.trim()).join(' — ') || undefined;
}
export function joinedReference(key: string, row: Row): string | undefined {
  if (key === 'plant_location_id' && plantLocationLabel(row)) return plantLocationLabel(row);
  const prefix = key.replace(/_ids?$/, '');
  const alias = aliases[prefix] ?? prefix;
  const fields = key.endsWith('_by')
    ? [`${key}_name`, `${key}_code`, `${key}_employee_code`]
    : [`${alias}_code`, `${alias}_number`, `${alias}_name`];
  if (prefix === 'batch_lot' || prefix === 'lot') fields.push('batch_code');
  if (['stored_file', 'source_file', 'evidence_file'].includes(prefix)) fields.push('file_name');
  const parts = fields
    .map((field) => text(row[field]))
    .filter((value) => value && !isOpaque(value));
  if (parts.length) return [...new Set(parts)].join(' · ');
  // A joined actor email is already scoped to this row. Use it when the name is
  // absent instead of requiring access to the administration user directory.
  if (key.endsWith('_by')) {
    const email = text(row[`${key}_email`]);
    if (email && !isOpaque(email)) return email;
  }
  const nested = object(row[prefix]);
  if (nested.id && nested.id !== row[key]) return undefined;
  const names = ['code', 'number', 'name', 'legal_name']
    .map((field) => text(nested[field]))
    .filter((value) => value && !isOpaque(value));
  return names.length ? names.join(' · ') : undefined;
}
/** Used for list actions and choices; internal keys remain only their submitted value. */
export function choiceLabel(row: Row, fields: string[], ordinal?: number): string {
  const parts = fields
    .map((field) => {
      const value = at(row, field);
      return isReference(field)
        ? joinedReference(field, row)
        : isOpaque(value)
          ? undefined
          : text(value);
    })
    .filter((part): part is string => Boolean(part));
  if (parts.length) return [...new Set(parts)].join(' · ');
  const identity = [
    'invoice_number',
    'shipment_number',
    'vendor_invoice_number',
    'document_number',
    'lot_code',
    'batch_code',
    'product_code',
    'file.file_name',
    'file_name',
    'name',
    'legal_name',
    'description',
  ]
    .map((field) => text(at(row, field)))
    .find((value) => value && !isOpaque(value));
  return (
    identity ||
    (ordinal === undefined ? 'Details unavailable' : `Record ${ordinal + 1} · details unavailable`)
  );
}
export function referenceTitle(key: string): string {
  return key
    .replace(/_ids?$/, '')
    .replaceAll('_', ' ')
    .replace(/^./, (character) => character.toUpperCase());
}
